import {
  createOrb,
  displayStateToConversationState,
} from "@moss-js/founding-agent/core";
import type { OrbHandle, VoiceAgentStore } from "@moss-js/founding-agent/core";
import type { StoreApi } from "zustand/vanilla";
import type { AgentAppearance } from "../config.js";

export interface OrbUIOpts {
  appearance: AgentAppearance;
  /** Square size in CSS px. Defaults to 48 (matches the aura canvas). */
  size?: number;
}

export interface OrbUIHandle {
  el: HTMLElement;
  setAppearance(appearance: AgentAppearance): void;
  destroy(): void;
}

/**
 * Mirror of `createAura`: mounts the portal-configured canvas orb and drives
 * its conversation state from the store. Reduced-motion, offscreen pausing
 * and the no-2d-context degrade all live in the core driver.
 */
export function createOrbUI(
  root: HTMLElement,
  store: StoreApi<VoiceAgentStore>,
  opts: OrbUIOpts,
): OrbUIHandle {
  const orb: OrbHandle = createOrb(root, {
    appearance: opts.appearance,
    state: displayStateToConversationState(store.getState().agentDisplayState),
    size: opts.size ?? 48,
  });

  const unsub = store.subscribe((state, prev) => {
    if (state.agentDisplayState !== prev.agentDisplayState) {
      orb.setState(displayStateToConversationState(state.agentDisplayState));
    }
  });

  return {
    el: orb.el,
    setAppearance(appearance) {
      orb.setAppearance(appearance);
    },
    destroy() {
      unsub();
      orb.destroy();
    },
  };
}
